import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { PlayIcon } from './BespokeIcons';

/**
 * BeforeWeBegin - Mandatory intro before Belonging experience
 * Beautiful, film-like void experience
 * Shows even when skip intro is used
 */
export const BeforeWeBegin = ({ onComplete }) => {
  const [fadeIn, setFadeIn] = useState(false);
  
  useEffect(() => {
    setFadeIn(true);
  }, []);
  
  return (
    <div 
      className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-2000 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
      data-testid="before-we-begin-screen"
    >
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900/30 to-black" />
      
      {/* Gentle pulsing light */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.03) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 max-w-2xl px-6 text-center space-y-8">
        <div className="space-y-6">
          <p 
            className="text-white/90 text-2xl sm:text-3xl leading-relaxed font-light tracking-wide"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="before-we-begin-message"
          >
            Now, we are welcome to honestly and empathetically explore how we are...
          </p>
          
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent mx-auto" />
          
          <p className="text-white/40 text-sm tracking-widest">
            A space for truth, without blame
          </p>
        </div>
        
        <Button
          onClick={onComplete}
          variant="ghost"
          className="text-amber-400/70 hover:text-amber-400 mt-12"
          data-testid="before-we-begin-continue-btn"
        >
          Begin
        </Button>
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
      `}</style>
    </div>
  );
};

/**
 * VisionQuestVideoIntro - Spiral video before Vision Quest begins
 * Plays spiralorg lowrez.mov with gentle skip option
 */
export const VisionQuestVideoIntro = ({ onComplete, onSkip }) => {
  const [showSkip, setShowSkip] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  
  useEffect(() => {
    // Show skip button after 5 seconds
    const timer = setTimeout(() => setShowSkip(true), 5000);
    return () => clearTimeout(timer);
  }, []);
  
  const handleVideoEnd = () => {
    setVideoEnded(true);
    setTimeout(onComplete, 1000);
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black z-50 flex items-center justify-center"
      data-testid="vision-quest-video-intro"
    >
      {/* Video */}
      <video
        autoPlay
        muted
        playsInline
        onEnded={handleVideoEnd}
        className="absolute inset-0 w-full h-full object-cover opacity-90"
        style={{ maxHeight: '100vh' }}
        data-testid="vision-quest-intro-video"
      >
        <source src="https://customer-assets.emergentagent.com/job_elchme-connect/artifacts/sxgxcak5_spiralorg%20lowrez.mov" type="video/quicktime" />
        <source src="https://customer-assets.emergentagent.com/job_elchme-connect/artifacts/sxgxcak5_spiralorg%20lowrez.mov" type="video/mp4" />
        Your browser does not support video playback.
      </video>
      
      {/* Gentle vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/60" />
      
      {/* Skip button - appears after 5 seconds */}
      {showSkip && !videoEnded && (
        <button
          onClick={onSkip}
          className="absolute bottom-8 right-8 px-4 py-2 text-white/50 hover:text-white/90 text-sm transition-all duration-300 hover:bg-white/5 rounded-lg backdrop-blur-sm"
          style={{ animation: 'fadeIn 1s ease-in' }}
          data-testid="vision-quest-skip-btn"
        >
          Skip →
        </button>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BeforeWeBegin;
